import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  FaWallet,
  FaPlus,
  FaSync,
  FaRupeeSign,
  FaCalendarAlt,
  FaTimes,
  FaCheckCircle,
  FaExclamationCircle,
  FaPencilAlt,
  FaEquals,
  FaMinus,
  FaHistory,
  FaArrowRight,
  FaTrash,
  FaSave,
} from "react-icons/fa";

const API_BASE_URL = 'https://omapi.omgoldfinance.live/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(value || 0);

const fmtPlain = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(value || 0);

const fmtDate = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const todayStr = () => new Date().toISOString().split("T")[0];

const longDate = () =>
  new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => (
  <div
    style={{
      position: "fixed",
      top: 24,
      right: 24,
      zIndex: 9999,
      background: type === "success" ? "#052e16" : "#450a0a",
      border: `1px solid ${type === "success" ? "#16a34a" : "#dc2626"}`,
      color: "#fff",
      padding: "13px 18px",
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      gap: 10,
      fontSize: 13,
      boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
      minWidth: 280,
      maxWidth: 400,
      animation: "toastIn 0.3s cubic-bezier(.175,.885,.32,1.275)",
    }}
  >
    {type === "success" ? (
      <FaCheckCircle style={{ color: "#22c55e", flexShrink: 0 }} />
    ) : (
      <FaExclamationCircle style={{ color: "#ef4444", flexShrink: 0 }} />
    )}
    <span style={{ flex: 1, lineHeight: 1.4 }}>{message}</span>
    <FaTimes style={{ cursor: "pointer", opacity: 0.6, flexShrink: 0 }} onClick={onClose} />
  </div>
);

// ─── Progress Ring ────────────────────────────────────────────────────────────
const ProgressRing = ({ percent, size = 120, stroke = 10 }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(percent, 100) / 100) * circ;
  const color = percent >= 90 ? "#ef4444" : percent >= 70 ? "#f59e0b" : "#22c55e";
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1e293b" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.6s ease, stroke 0.4s ease" }}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="middle"
        fill={color}
        fontSize={size * 0.18}
        fontWeight="700"
        style={{ transform: "rotate(90deg)", transformOrigin: "center", fontFamily: "Inter" }}
      >
        {percent.toFixed(1)}%
      </text>
    </svg>
  );
};

// ─── Inline Amount Input ──────────────────────────────────────────────────────
const AmountInput = ({ value, onChange, onSubmit, loading, placeholder, autoFocus }) => {
  const ref = useRef(null);
  useEffect(() => { if (autoFocus && ref.current) ref.current.focus(); }, [autoFocus]);

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <div
        style={{
          position: "relative",
          flex: 1,
          maxWidth: 280,
        }}
      >
        <span
          style={{
            position: "absolute",
            left: 13,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#4da6ff",
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          ₹
        </span>
        <input
          ref={ref}
          type="number"
          min="0"
          step="1000"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSubmit()}
          placeholder={placeholder || "Enter amount"}
          style={{
            width: "100%",
            background: "#0f172a",
            border: "1.5px solid #334155",
            borderRadius: 10,
            padding: "11px 12px 11px 30px",
            color: "#f1f5f9",
            fontSize: 16,
            fontWeight: 600,
            fontFamily: "Inter, sans-serif",
            boxSizing: "border-box",
            transition: "border-color 0.2s",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#4da6ff")}
          onBlur={(e) => (e.target.style.borderColor = "#334155")}
        />
      </div>
      <button
        onClick={onSubmit}
        disabled={loading}
        style={{
          background: "linear-gradient(135deg, #4da6ff, #3b82f6)",
          border: "none",
          color: "#fff",
          borderRadius: 10,
          padding: "11px 20px",
          cursor: loading ? "not-allowed" : "pointer",
          fontSize: 13,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "nowrap",
          opacity: loading ? 0.7 : 1,
          transition: "all 0.2s",
        }}
      >
        {loading ? <FaSync style={{ animation: "spin 1s linear infinite" }} /> : <FaCheckCircle />}
        {loading ? "Saving…" : "Confirm"}
      </button>
    </div>
  );
};

// ─── Formula Block ─────────────────────────────────────────────────────────────
const FormulaRow = ({ data }) => {
  const items = [
    { label: "Opening Balance", value: data.openingBalance, color: "#4da6ff", sign: null },
    { label: "Purchases (Stock In)", value: data.addedAmount, color: "#a78bfa", sign: <FaPlus style={{ color: "#64748b", fontSize: 11 }} /> },
    { label: "Sales (Stock Out)", value: data.utilizedAmount, color: "#f87171", sign: <FaMinus style={{ color: "#64748b", fontSize: 11 }} /> },
    { label: "Current Balance", value: data.closingBalance, color: "#22c55e", sign: <FaEquals style={{ color: "#64748b", fontSize: 11 }} /> },
  ];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: 0,
        background: "#0f172a",
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid #1e3a5f",
      }}
    >
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          {item.sign && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "0 4px",
                background: "#0f172a",
              }}
            >
              {item.sign}
            </div>
          )}
          <div
            style={{
              flex: 1,
              padding: "18px 16px",
              borderLeft: i > 0 && !item.sign ? "1px solid #1e293b" : "none",
              display: "flex",
              flexDirection: "column",
              gap: 4,
              background: i === 3 ? `${item.color}0d` : "transparent",
            }}
          >
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              {item.label}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: item.color }}>
              {fmt(item.value)}
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DailyBalance = () => {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [histLoading, setHistLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // UI states
  const [mode, setMode] = useState("view"); // view | setInitial | editAmount | addMore
  const [inputAmount, setInputAmount] = useState("");
  const [inputNotes, setInputNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // History filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Edit/Delete history state
  const [editingRow, setEditingRow] = useState(null); // { id, openingBalance, notes, date }
  const [editAmount, setEditAmount] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, date }
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Auto-refresh ref
  const refreshTimer = useRef(null);

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ── Fetch today ──────────────────────────────────────────────────────────
  const fetchToday = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await api.get('/daily-balance/today');
      if (res.data.success) {
        setData(res.data.data);
      }
    } catch {
      if (!silent) showToast("Failed to load today's balance", "error");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [showToast]);

  // ── Fetch history ──────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async (page = 1) => {
    try {
      setHistLoading(true);
      const params = { page, per_page: 10 };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await api.get('/daily-balance/history', { params });
      if (res.data.success) {
        setHistory(res.data.data);
        setPagination(res.data.pagination);
      }
    } catch {
      showToast("Failed to load history", "error");
    } finally {
      setHistLoading(false);
    }
  }, [fromDate, toDate, showToast]);

  useEffect(() => {
    fetchToday();
    fetchHistory();
    // Auto-refresh every 60s to sync new bills
    refreshTimer.current = setInterval(() => fetchToday(true), 60000);
    return () => clearInterval(refreshTimer.current);
  }, [fetchToday, fetchHistory]);

  // ── SET the initial billing amount for today ──────────────────────────────
  const handleSetAmount = async () => {
    const amt = parseFloat(inputAmount);
    if (isNaN(amt) || amt < 0) {
      showToast("Please enter a valid amount (0 or more)", "error");
      return;
    }
    try {
      setActionLoading(true);
      const res = await api.post('/daily-balance/set-amount', {
        amount: amt,
        notes: inputNotes || null,
      });
      if (res.data.success) {
        setData(res.data.data);
        showToast(`Billing amount set to ${fmt(amt)}`);
        setMode("view");
        setInputAmount("");
        setInputNotes("");
        fetchHistory();
      } else {
        showToast(res.data.message || "Failed to set amount", "error");
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to set amount", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // ── ADD more billing amount (top-up) ──────────────────────────────────────
  const handleAddAmount = async () => {
    const amt = parseFloat(inputAmount);
    if (isNaN(amt) || amt <= 0) {
      showToast("Please enter an amount greater than 0", "error");
      return;
    }
    try {
      setActionLoading(true);
      const res = await api.post('/daily-balance/add-amount', {
        amount: amt,
        notes: inputNotes || "",
      });
      if (res.data.success) {
        setData(res.data.data);
        showToast(`Added ${fmt(amt)} to today's balance`);
        setMode("view");
        setInputAmount("");
        setInputNotes("");
        fetchHistory();
      } else {
        showToast(res.data.message || "Failed to add amount", "error");
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to add amount", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Sync bills ────────────────────────────────────────────────────────────
  const handleSync = async () => {
    try {
      setActionLoading(true);
      const res = await api.post('/daily-balance/recalculate', {
        date: todayStr(),
      });
      if (res.data.success) {
        setData(res.data.data);
        showToast("Balance synced from latest bills");
      }
    } catch {
      showToast("Sync failed", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Edit a history record ──────────────────────────────────────────────────
  const handleEditSave = async () => {
    const amt = parseFloat(editAmount);
    if (isNaN(amt) || amt < 0) {
      showToast("Please enter a valid opening balance (0 or more)", "error");
      return;
    }
    try {
      setEditLoading(true);
      const res = await api.put(
        `/daily-balance/${editingRow.id}`,
        { opening_balance: amt, notes: editNotes }
      );
      if (res.data.success) {
        showToast(`Balance record for ${fmtDate(editingRow.date)} updated`);
        setEditingRow(null);
        fetchHistory(pagination.page);
        fetchToday(true);
      } else {
        showToast(res.data.message || "Failed to update record", "error");
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to update record", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // ── Delete a history record ────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    try {
      setDeleteLoading(true);
      const res = await api.delete(
        `/daily-balance/${deleteConfirm.id}`
      );
      if (res.data.success) {
        showToast(`Balance record for ${fmtDate(deleteConfirm.date)} deleted`);
        setDeleteConfirm(null);
        fetchHistory(pagination.page);
        fetchToday(true);
      } else {
        showToast(res.data.message || "Failed to delete record", "error");
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete record", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelInput = () => {
    setMode("view");
    setInputAmount("");
    setInputNotes("");
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const utilPct = data && data.totalAvailable > 0
    ? (data.utilizedAmount / data.totalAvailable) * 100
    : 0;
  const hasAmountSet = true;
  const utilColor = utilPct >= 90 ? "#ef4444" : utilPct >= 70 ? "#f59e0b" : "#22c55e";


  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", color: "#f1f5f9", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        @keyframes toastIn { from { transform: translateX(80px); opacity:0 } to { transform:translateX(0); opacity:1 } }
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .hist-row:hover td { background: rgba(77,166,255,0.05) !important; }
        .action-btn:hover { filter: brightness(1.1); transform: translateY(-1px); }
        input[type=number]::-webkit-inner-spin-button { display:none; }
        .edit-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.7); z-index:10000; display:flex; align-items:center; justify-content:center; animation:fadeIn 0.2s ease; }
        .del-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.7); z-index:10000; display:flex; align-items:center; justify-content:center; animation:fadeIn 0.2s ease; }
      `}</style>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* ── EDIT HISTORY MODAL ──────────────────────────────────────────────── */}
      {editingRow && (
        <div className="edit-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setEditingRow(null); }}>
          <div style={{ background: "#1e293b", border: "1px solid #4da6ff44", borderRadius: 18, padding: "28px 28px 22px", minWidth: 360, maxWidth: 480, width: "90%", boxShadow: "0 24px 80px rgba(0,0,0,0.6)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0" }}>Edit Balance Record</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{fmtDate(editingRow.date)}</div>
              </div>
              <button onClick={() => setEditingRow(null)} style={{ background: "transparent", border: "none", color: "#475569", cursor: "pointer", fontSize: 18, padding: 4 }}><FaTimes /></button>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>Opening Balance (₹)</label>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "#4da6ff", fontSize: 15, fontWeight: 600 }}>₹</span>
                <input
                  type="number"
                  min="0"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleEditSave()}
                  style={{ width: "100%", background: "#0f172a", border: "1.5px solid #334155", borderRadius: 10, padding: "11px 12px 11px 30px", color: "#f1f5f9", fontSize: 16, fontWeight: 600, fontFamily: "Inter, sans-serif", boxSizing: "border-box" }}
                  autoFocus
                />
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>Notes</label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Notes (optional)"
                rows={2}
                style={{ width: "100%", background: "#0f172a", border: "1px solid #334155", borderRadius: 8, padding: "9px 12px", color: "#94a3b8", fontSize: 13, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={handleEditSave}
                disabled={editLoading}
                style={{ flex: 1, background: "linear-gradient(135deg, #4da6ff, #3b82f6)", border: "none", color: "#fff", borderRadius: 10, padding: "11px 18px", cursor: editLoading ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: editLoading ? 0.7 : 1 }}
              >
                {editLoading ? <FaSync style={{ animation: "spin 1s linear infinite" }} /> : <FaSave />}
                {editLoading ? "Saving…" : "Save Changes"}
              </button>
              <button onClick={() => setEditingRow(null)} style={{ background: "transparent", border: "1px solid #334155", color: "#64748b", borderRadius: 10, padding: "11px 18px", cursor: "pointer", fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ────────────────────────────────────────────── */}
      {deleteConfirm && (
        <div className="del-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setDeleteConfirm(null); }}>
          <div style={{ background: "#1e293b", border: "1px solid #ef444440", borderRadius: 18, padding: "28px", minWidth: 340, maxWidth: 440, width: "90%", boxShadow: "0 24px 80px rgba(0,0,0,0.6)" }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#450a0a", border: "1px solid #dc2626", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <FaTrash style={{ color: "#ef4444", fontSize: 20 }} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0", marginBottom: 8 }}>Delete Balance Record?</div>
              <div style={{ fontSize: 13, color: "#64748b" }}>This will permanently delete the record for <strong style={{ color: "#f1f5f9" }}>{fmtDate(deleteConfirm.date)}</strong>. This action cannot be undone.</div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                style={{ flex: 1, background: "linear-gradient(135deg, #ef4444, #dc2626)", border: "none", color: "#fff", borderRadius: 10, padding: "11px 18px", cursor: deleteLoading ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: deleteLoading ? 0.7 : 1 }}
              >
                {deleteLoading ? <FaSync style={{ animation: "spin 1s linear infinite" }} /> : <FaTrash />}
                {deleteLoading ? "Deleting…" : "Yes, Delete"}
              </button>
              <button onClick={() => setDeleteConfirm(null)} style={{ background: "transparent", border: "1px solid #334155", color: "#64748b", borderRadius: 10, padding: "11px 18px", cursor: "pointer", fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Page Header ────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: "linear-gradient(135deg, #4da6ff, #6366f1)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 20px #4da6ff44" }}>
            <FaWallet style={{ fontSize: 20, color: "#fff" }} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#f8fafc", letterSpacing: "-0.3px" }}>
              Daily Billing Balance
            </h1>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>{longDate()}</p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            id="btn-sync-bills"
            onClick={handleSync}
            disabled={actionLoading}
            className="action-btn"
            style={{ display: "flex", alignItems: "center", gap: 7, background: "transparent", border: "1px solid #334155", color: "#94a3b8", padding: "9px 16px", borderRadius: 9, cursor: "pointer", fontSize: 13, transition: "all 0.2s" }}
          >
            <FaSync style={{ animation: actionLoading ? "spin 1s linear infinite" : "none" }} />
            Sync Bills
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 80, color: "#64748b" }}>
          <FaSync style={{ fontSize: 32, animation: "spin 1s linear infinite", color: "#4da6ff" }} />
          <p style={{ marginTop: 12, fontSize: 14 }}>Loading today's balance…</p>
        </div>
      ) : (
        <div style={{ animation: "fadeIn 0.4s ease" }}>

          {/* ── CARRY-FORWARD BANNER ─────────────────────────────────────── */}
          {data && data.openingBalance > 0 && (
            <div style={{ background: "linear-gradient(135deg, #0c1a2e, #111827)", border: "1px solid #1e3a5f", borderRadius: 12, padding: "14px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#4da6ff18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <FaArrowRight style={{ color: "#4da6ff", fontSize: 13 }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#4da6ff" }}>Balance Carried Forward from Previous Day</div>
                <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 2 }}>
                  <span style={{ color: "#f1f5f9", fontWeight: 700 }}>{fmt(data.openingBalance)}</span> has been added to today's available balance
                </div>
              </div>
            </div>
          )}



          {/* ── INPUT PANEL (setInitial or editAmount or addMore) ─────────── */}
          {mode !== "view" && (
            <div
              style={{
                background: "linear-gradient(135deg, #1e293b, #162032)",
                border: "1px solid #4da6ff44",
                borderRadius: 16,
                padding: "24px 24px 20px",
                marginBottom: 24,
                animation: "fadeIn 0.3s ease",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0" }}>
                    {mode === "addMore" ? "Add to Opening Balance" : "Set Opening Balance"}
                  </div>
                  {mode === "addMore" && data && (
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
                      Current: {fmt(data.openingBalance)} → New total will be {fmt((data.openingBalance || 0) + (parseFloat(inputAmount) || 0))}
                    </div>
                  )}
                </div>
                <button onClick={cancelInput} style={{ background: "transparent", border: "none", color: "#475569", cursor: "pointer", fontSize: 16, padding: 4 }}>
                  <FaTimes />
                </button>
              </div>

              <AmountInput
                value={inputAmount}
                onChange={setInputAmount}
                onSubmit={mode === "addMore" ? handleAddAmount : handleSetAmount}
                loading={actionLoading}
                placeholder={mode === "addMore" ? "Amount to add" : "e.g. 1000000"}
                autoFocus
              />

              {/* Preview calculation */}
              {inputAmount && !isNaN(parseFloat(inputAmount)) && data && (
                <div style={{ marginTop: 14, background: "#0f172a", borderRadius: 10, padding: "12px 16px", fontSize: 13 }}>
                  <div style={{ color: "#64748b", marginBottom: 8, fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Preview
                  </div>
                  {mode === "addMore" ? (
                    <>
                      <Row label="Opening Balance" val={fmt(data.openingBalance)} color="#4da6ff" />
                      <Row label={`+ Added to Opening`} val={fmt(parseFloat(inputAmount))} color="#a78bfa" />
                      <Row label="= New Opening Balance" val={fmt((data.openingBalance || 0) + parseFloat(inputAmount))} color="#22c55e" bold />
                    </>
                  ) : (
                    <>
                      <Row label="Opening Balance" val={fmt(parseFloat(inputAmount))} color="#22c55e" bold />
                    </>
                  )}
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <textarea
                  id="input-notes"
                  value={inputNotes}
                  onChange={(e) => setInputNotes(e.target.value)}
                  placeholder="Notes (optional)"
                  rows={2}
                  style={{ width: "100%", background: "#0f172a", border: "1px solid #334155", borderRadius: 8, padding: "9px 12px", color: "#94a3b8", fontSize: 13, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
                />
              </div>
            </div>
          )}

          {/* ── BALANCE DASHBOARD (when amount is set) ───────────────────── */}
          {data && hasAmountSet && (
            <>
              {/* Formula row */}
              <div style={{ marginBottom: 20 }}>
                <FormulaRow data={data} />
              </div>

              {/* Main card */}
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 20, marginBottom: 24, alignItems: "start" }}>
                {/* Progress ring */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #1e293b, #162032)",
                    border: "1px solid #1e3a5f",
                    borderRadius: 16,
                    padding: "28px 24px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 10,
                    minWidth: 180,
                  }}
                >
                  <ProgressRing percent={utilPct} size={130} stroke={11} />
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Utilization
                    </div>
                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                      {fmt(data.utilizedAmount)} of {fmt(data.totalAvailable)}
                    </div>
                  </div>
                </div>

                {/* Right panel */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {/* Available balance */}
                  <div
                    style={{
                      background: `linear-gradient(135deg, ${data.closingBalance <= 0 ? "#1c0f0f" : "#0a1f12"}, #162032)`,
                      border: `1px solid ${data.closingBalance <= 0 ? "#7f1d1d" : "#14532d"}`,
                      borderRadius: 14,
                      padding: "20px 22px",
                    }}
                  >
                    <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>
                      Remaining Balance {data.closingBalance <= 0 ? "(Exceeded)" : "(Carry Forward Tomorrow)"}
                    </div>
                    <div style={{ fontSize: 34, fontWeight: 800, color: data.closingBalance <= 0 ? "#ef4444" : "#22c55e", letterSpacing: "-0.5px" }}>
                      {fmt(data.closingBalance)}
                    </div>
                    {data.closingBalance <= 0 && data.utilizedAmount > data.totalAvailable && (
                      <div style={{ fontSize: 12, color: "#f87171", marginTop: 6 }}>
                        Exceeded by {fmt(data.utilizedAmount - data.totalAvailable)}
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  {mode === "view" && (
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <button
                        id="btn-add-more"
                        onClick={() => { setMode("addMore"); setInputAmount(""); }}
                        className="action-btn"
                        style={{ flex: 1, minWidth: 140, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "linear-gradient(135deg, #4da6ff, #3b82f6)", border: "none", color: "#fff", padding: "11px 18px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600, transition: "all 0.2s" }}
                      >
                        <FaPlus /> Add to Opening Balance
                      </button>
                      <button
                        id="btn-edit-amount"
                        onClick={() => { setMode("editAmount"); setInputAmount(String(data.openingBalance || "")); }}
                        className="action-btn"
                        style={{ display: "flex", alignItems: "center", gap: 7, background: "transparent", border: "1px solid #334155", color: "#94a3b8", padding: "11px 18px", borderRadius: 10, cursor: "pointer", fontSize: 13, transition: "all 0.2s" }}
                      >
                        <FaPencilAlt style={{ fontSize: 11 }} /> Edit Opening Balance
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* ── END-OF-DAY SUMMARY ──────────────────────────────────── */}
              <div
                style={{
                  background: "linear-gradient(135deg, #1e293b, #162032)",
                  border: "1px solid #1e3a5f",
                  borderRadius: 14,
                  padding: "20px 22px",
                  marginBottom: 28,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 16 }}>
                  Day Summary
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                  {[
                    { label: "Opening Balance", val: fmt(data.openingBalance), color: "#4da6ff" },
                    { label: "Purchases (Stock In)", val: fmt(data.addedAmount), color: "#a78bfa" },
                    { label: "Total Available", val: fmt(data.totalAvailable), color: "#e2e8f0" },
                    { label: "Sales (Stock Out)", val: fmt(data.utilizedAmount), color: "#f87171" },
                    { label: "Current Balance", val: fmt(data.closingBalance), color: data.closingBalance > 0 ? "#22c55e" : "#ef4444" },
                  ].map((item) => (
                    <div
                      key={item.label}
                      style={{ background: "#0f172a", borderRadius: 10, padding: "13px 14px", border: `1px solid ${item.color}20` }}
                    >
                      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 5 }}>{item.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: item.color }}>{item.val}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── HISTORY TABLE ─────────────────────────────────────────────────── */}
      <div style={{ background: "linear-gradient(135deg, #1e293b, #162032)", border: "1px solid #1e3a5f", borderRadius: 14, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #1e3a5f", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaHistory style={{ color: "#4da6ff", fontSize: 14 }} />
            <span style={{ fontWeight: 700, color: "#e2e8f0", fontSize: 14 }}>Balance History</span>
            {pagination.total > 0 && (
              <span style={{ background: "#1e3a5f", color: "#64748b", fontSize: 11, padding: "2px 8px", borderRadius: 99 }}>
                {pagination.total} days
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <input id="hist-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              style={{ background: "#0f172a", border: "1px solid #334155", color: "#94a3b8", borderRadius: 8, padding: "7px 10px", fontSize: 12 }} />
            <span style={{ color: "#475569", fontSize: 12 }}>to</span>
            <input id="hist-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              style={{ background: "#0f172a", border: "1px solid #334155", color: "#94a3b8", borderRadius: 8, padding: "7px 10px", fontSize: 12 }} />
            <button id="btn-filter" onClick={() => fetchHistory(1)} className="action-btn"
              style={{ background: "#4da6ff", border: "none", color: "#fff", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 5, transition: "all 0.2s" }}>
              <FaCalendarAlt style={{ fontSize: 11 }} /> Filter
            </button>
            {(fromDate || toDate) && (
              <button onClick={() => { setFromDate(""); setToDate(""); setTimeout(() => fetchHistory(1), 50); }}
                style={{ background: "transparent", border: "1px solid #334155", color: "#64748b", borderRadius: 8, padding: "7px 10px", cursor: "pointer", fontSize: 12 }}>
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#0a1628" }}>
                {["Date", "Carry Forward", "Amount Added", "Total Available", "Deducted", "Balance", "Utilization", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {histLoading ? (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#64748b" }}>
                  <FaSync style={{ animation: "spin 1s linear infinite", marginRight: 8 }} /> Loading…
                </td></tr>
              ) : history.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 48, color: "#64748b", fontSize: 13 }}>
                  No records found
                </td></tr>
              ) : history.map((row) => {
                const pct = row.totalAvailable > 0 ? Math.min((row.utilizedAmount / row.totalAvailable) * 100, 100) : 0;
                const rc = pct >= 90 ? "#ef4444" : pct >= 70 ? "#f59e0b" : "#22c55e";
                const isToday = row.date === todayStr();
                return (
                  <tr key={row.id} className="hist-row" style={{ borderTop: "1px solid #1e293b" }}>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {isToday && <span style={{ background: "#4da6ff20", color: "#4da6ff", fontSize: 10, padding: "2px 7px", borderRadius: 99, fontWeight: 600 }}>Today</span>}
                        <span style={{ color: "#e2e8f0", fontSize: 13, fontWeight: isToday ? 700 : 400 }}>{fmtDate(row.date)}</span>
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#4da6ff" }}>{fmt(row.openingBalance)}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#a78bfa" }}>{fmt(row.addedAmount)}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#e2e8f0", fontWeight: 600 }}>{fmt(row.totalAvailable)}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: rc }}>{fmt(row.utilizedAmount)}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: row.closingBalance > 0 ? "#22c55e" : "#ef4444", fontWeight: 700 }}>{fmt(row.closingBalance)}</td>
                    <td style={{ padding: "12px 16px", minWidth: 120 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, background: "#1e293b", borderRadius: 99, height: 6, overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: rc, borderRadius: 99, transition: "width 0.4s ease" }} />
                        </div>
                        <span style={{ fontSize: 12, color: rc, minWidth: 36, textAlign: "right" }}>{pct.toFixed(1)}%</span>
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          title="Edit record"
                          onClick={() => {
                            setEditingRow({ id: row.id, date: row.date });
                            setEditAmount(String(row.openingBalance || ""));
                            setEditNotes(row.notes || "");
                          }}
                          style={{ background: "#1e3a5f", border: "1px solid #4da6ff40", color: "#4da6ff", borderRadius: 7, padding: "6px 10px", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 5, transition: "all 0.15s" }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#1e4a7f"}
                          onMouseLeave={(e) => e.currentTarget.style.background = "#1e3a5f"}
                        >
                          <FaPencilAlt style={{ fontSize: 11 }} /> Edit
                        </button>
                        <button
                          title="Delete record"
                          onClick={() => setDeleteConfirm({ id: row.id, date: row.date })}
                          style={{ background: "#450a0a", border: "1px solid #ef444440", color: "#ef4444", borderRadius: 7, padding: "6px 10px", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 5, transition: "all 0.15s" }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#7f1d1d"}
                          onMouseLeave={(e) => e.currentTarget.style.background = "#450a0a"}
                        >
                          <FaTrash style={{ fontSize: 11 }} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={{ padding: "12px 22px", borderTop: "1px solid #1e293b", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "#64748b" }}>
            <span>{pagination.total} total records</span>
            <div style={{ display: "flex", gap: 6 }}>
              {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => fetchHistory(p)}
                  style={{ background: p === pagination.page ? "#4da6ff" : "transparent", border: "1px solid #334155", color: p === pagination.page ? "#fff" : "#94a3b8", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12 }}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── tiny helper row ────────────────────────────────────────────────────────────
const Row = ({ label, val, color, bold }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderTop: bold ? "1px solid #1e293b" : "none", marginTop: bold ? 6 : 0, paddingTop: bold ? 8 : 3 }}>
    <span style={{ color: "#64748b", fontSize: 12 }}>{label}</span>
    <span style={{ color: color || "#e2e8f0", fontWeight: bold ? 700 : 500, fontSize: 13 }}>{val}</span>
  </div>
);

export default DailyBalance;
